import { DateInput, Field, MoneyInput, Select, TextInput } from '../components/index.js';
import { fromApi, isErr, money, useFields, useSave } from '../lib/form.js';
import { t } from '../lib/i18n.js';
import { createAccount, master, today, updateAccount } from '../lib/khata.js';
import { FormErrorLine, SaveButton, Sheet } from './Sheet.js';

export function AccountSheet(props: { id?: string }) {
  const existing = props.id ? master.value.accounts.get(props.id) : undefined;
  const [f, set] = useFields({
    name: existing?.name ?? '',
    kind: existing?.kind ?? 'bank',
    opening: '',
    openDate: today.value,
    active: existing?.active === false ? 'no' : 'yes',
  });
  const [error, busy, save] = useSave();

  const onSave = () =>
    save(async () => {
      if (!f.name.trim()) return { message: t('account_needsName'), field: 'name' };
      if (existing) {
        const r = await updateAccount(existing.id, {
          name: f.name,
          kind: f.kind,
          active: f.active === 'yes',
        });
        return r.ok ? t('account_saved', { name: r.value.name }) : fromApi(r.error);
      }
      let opening: { date: string; amount: number } | undefined;
      if (f.opening.trim() !== '') {
        const a = money(f.opening, 'opening');
        if (isErr(a)) return a;
        opening = { date: f.openDate, amount: a };
      }
      const r = await createAccount({ name: f.name, kind: f.kind }, opening);
      return r.ok ? t('account_added', { name: r.value.account.name }) : fromApi(r.error);
    });

  return (
    <Sheet
      title={existing ? t('sheet_editAccount') : t('sheet_newAccount')}
      footer={<SaveButton busy={busy} onClick={onSave} />}
    >
      <Field label={t('item_name')}>
        <TextInput
          value={f.name}
          onInput={(v) => set('name', v)}
          placeholder={t('account_namePlaceholder')}
          invalid={error?.field === 'name'}
          autoFocus={!existing}
        />
      </Field>
      <div class="grid-2">
        <Field label={t('account_kind')}>
          <Select value={f.kind} onChange={(v) => set('kind', v)} invalid={error?.field === 'kind'}>
            <option value="bank">{t('account_kindBank')}</option>
            <option value="mobile">{t('account_kindMobile')}</option>
            <option value="cash">{t('account_kindCash')}</option>
          </Select>
        </Field>
        {existing ? (
          <Field label={t('account_status')}>
            <Select value={f.active} onChange={(v) => set('active', v)}>
              <option value="yes">{t('account_open')}</option>
              <option value="no">{t('account_closed')}</option>
            </Select>
          </Field>
        ) : (
          <Field label={t('account_opening')}>
            <MoneyInput
              value={f.opening}
              onInput={(v) => set('opening', v)}
              invalid={error?.field === 'opening'}
            />
          </Field>
        )}
      </div>
      {!existing && (
        <Field label={t('item_asOf')}>
          <DateInput value={f.openDate} onInput={(v) => set('openDate', v)} />
        </Field>
      )}
      <FormErrorLine error={error} />
    </Sheet>
  );
}
